import React, { useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from "@mui/material";
import DialogHeader from "../../../components/Dialog/DialogHeader";
import UserDialog from "../../../components/Dialog/UserDialog";
import { CreateEquipmentDto, EquipmentDict } from "../../../model/Dto/Dict/Equipment";
import { EquipmentCategory } from "../../../model/Dto/Dict/EquipmentCategory";

interface EquipmentDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: CreateEquipmentDto) => Promise<boolean>;
    initialData?: EquipmentDict | null;
    title: string;
    categories: EquipmentCategory[];
}

export default function EquipmentDialog({
    open,
    onClose,
    onSave,
    initialData,
    title,
    categories,
}: EquipmentDialogProps) {
    const [name, setName] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [cost, setCost] = useState("0");
    const [rarity, setRarity] = useState("");
    const [description, setDescription] = useState("");
    const [isAvailableOnTradingPost, setIsAvailableOnTradingPost] = useState(false);
    const [isArmour, setIsArmour] = useState(false);
    const [countInRating, setCountInRating] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (initialData) {
            setName(initialData.name);
            setCategoryId(initialData.categoryId);
            setCost(String(initialData.cost));
            setRarity(initialData.rarity != null ? String(initialData.rarity) : "");
            setDescription(initialData.description ?? "");
            setIsAvailableOnTradingPost(initialData.isAvailableOnTradingPost);
            setIsArmour(initialData.isArmour);
            setCountInRating(initialData.countInRating);
        } else {
            setName("");
            setCategoryId(categories.length > 0 ? categories[0].id : "");
            setCost("0");
            setRarity("");
            setDescription("");
            setIsAvailableOnTradingPost(false);
            setIsArmour(false);
            setCountInRating(false);
        }
    }, [initialData, open, categories]);

    const handleSave = async () => {
        setLoading(true);
        const success = await onSave({
            name: name.trim(),
            categoryId,
            cost: parseInt(cost) || 0,
            rarity: rarity.trim() !== "" ? parseInt(rarity) : null,
            description: description.trim(),
            isAvailableOnTradingPost,
            isArmour,
            countInRating,
        });
        setLoading(false);
        if (success) onClose();
    };

    return (
        <UserDialog handleClose={onClose} open={open} maxWidth="sm">
            <DialogHeader loading={loading} title={title} />
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <TextField
                        autoFocus
                        label="Название"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={loading}
                    />
                    <FormControl fullWidth disabled={loading}>
                        <InputLabel>Категория</InputLabel>
                        <Select
                            value={categoryId}
                            label="Категория"
                            onChange={(e) => setCategoryId(e.target.value)}
                        >
                            {categories.map((c) => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Стоимость"
                                fullWidth
                                inputProps={{ inputMode: "numeric" }}
                                value={cost}
                                onChange={(e) => setCost(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Редкость"
                                fullWidth
                                inputProps={{ inputMode: "numeric" }}
                                value={rarity}
                                onChange={(e) => setRarity(e.target.value)}
                                disabled={loading}
                                placeholder="Не указана"
                            />
                        </Grid>
                    </Grid>
                    <TextField
                        label="Описание"
                        fullWidth
                        multiline
                        minRows={2}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={loading}
                    />
                    <Grid container>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAvailableOnTradingPost}
                                        onChange={(e) => setIsAvailableOnTradingPost(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Доступно на торговом посту"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isArmour}
                                        onChange={(e) => setIsArmour(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Броня"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={countInRating}
                                        onChange={(e) => setCountInRating(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Учитывать в рейтинге"
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Отмена</Button>
                <Button onClick={handleSave} variant="contained" disabled={loading || !categoryId}>
                    {loading ? "Сохранение..." : "Сохранить"}
                </Button>
            </DialogActions>
        </UserDialog>
    );
}
